import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import { Users, FileText, DollarSign, Package, Download, Eye, Search, Filter, Trash2, MessageSquare, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [respondingToTicket, setRespondingToTicket] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [settingPayment, setSettingPayment] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const ADMIN_DELETE_PASSWORD = '1Friday123456';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const [statsRes, clientsRes, ticketsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { 
          headers 
        }),
        fetch(`${BACKEND_URL}/api/admin/clients`, { 
          headers 
        }),
        fetch(`${BACKEND_URL}/api/admin/tickets`, { 
          headers 
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
        setFilteredClients(clientsData);
      } else {
        console.error('Failed to load clients:', clientsRes.status, await clientsRes.text());
      }

      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setTickets(ticketsData);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  const filterClients = useCallback(() => {
    let filtered = clients;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.mobile.includes(searchTerm) ||
        client.client_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(client => client.total_paid_orders > 0);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(client => client.total_paid_orders === 0);
    }

    setFilteredClients(filtered);
  }, [clients, searchTerm, filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterClients();
  }, [filterClients]);

  const viewClientDetails = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${clientId}`, {
        headers
      });
      if (res.ok) {
        const data = await res.json();
        setClientDetails(data);
        setSelectedClient(clientId);
      }
    } catch (error) {
      console.error('Error loading client details:', error);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('📥 Starting download:', { documentId, filename, backend: BACKEND_URL });
      
      const downloadUrl = `${BACKEND_URL}/api/admin/documents/${documentId}/download`;
      console.log('📍 Request URL:', downloadUrl);
      
      const response = await fetch(downloadUrl, { headers });
      
      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Try to parse error as JSON first, fallback to text
        let errorMessage = `HTTP ${response.status}`;
        let errorDetail = '';
        try {
          const contentType = response.headers.get('content-type');
          console.log('❌ Error content-type:', contentType);
          
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.log('❌ Error data:', errorData);
            errorMessage = errorData.detail || errorData.message || errorMessage;
            errorDetail = errorData.detail || '';
          } else {
            const errorText = await response.text();
            console.log('❌ Error text:', errorText);
            errorMessage = errorText || errorMessage;
          }
        } catch (parseError) {
          console.warn('⚠️ Could not parse error response:', parseError);
        }
        
        console.error('❌ Download failed:', response.status, errorMessage);
        
        // More user-friendly error message
        if (response.status === 404) {
          throw new Error(`File not found on server.\n\nDocument ID: ${documentId}\nFilename: ${filename}\n\nDetails: ${errorDetail || 'File may have been moved or deleted'}`);
        } else if (response.status === 403) {
          throw new Error('Access denied. Admin permissions required.');
        } else {
          throw new Error(`Download failed (${response.status}): ${errorMessage}`);
        }
      }

      const blob = await response.blob();
      console.log('✅ Downloaded blob size:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty (0 bytes)');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download successful:', filename);
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Download Failed\n\n${error.message}\n\nTip: Open browser console (F12) for more details`);
    }
  };

  const markOrderAsUnpaid = async (serviceRequestId) => {
    if (!window.confirm('Are you sure you want to mark this order as UNPAID? The user will be able to delete this service request.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/admin/orders/${serviceRequestId}/mark-unpaid`,
        {
          method: 'POST',
          headers
        }
      );

      if (response.ok) {
        alert('Order marked as unpaid successfully!');
        // Reload client details
        if (selectedClient) {
          viewClientDetails(selectedClient);
        }
        // Reload all clients data
        loadData();
      } else {
        throw new Error('Failed to mark as unpaid');
      }
    } catch (error) {
      console.error('Error marking order as unpaid:', error);
      alert('Failed to mark order as unpaid. Please try again.');
    }
  };

  const handleSetPayment = (order) => {
    setSelectedOrder(order);
    setCustomAmount(order.custom_amount || '');
    setPaymentLink(order.payment_link || '');
    setShowPaymentModal(true);
  };

  const submitPaymentDetails = async () => {
    if (!customAmount) {
      alert('Please enter the fee amount');
      return;
    }

    setSettingPayment(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/set-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            custom_amount: parseFloat(customAmount)
          })
        }
      );

      if (response.ok) {
        alert('Custom fees set successfully! QR code will be auto-generated. User will receive email notification.');
        setShowPaymentModal(false);
        setCustomAmount('');
        setPaymentLink('');
        // Reload client details
        if (selectedClient) {
          viewClientDetails(selectedClient);
        }
      } else {
        throw new Error('Failed to set payment details');
      }
    } catch (error) {
      console.error('Error setting payment:', error);
      alert('Failed to set custom fees. Please try again.');
    } finally {
      setSettingPayment(false);
    }
  };


  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setDeletePassword('');
    setPasswordError('');
    setShowDeleteDialog(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;

    // Validate password first
    if (deletePassword !== ADMIN_DELETE_PASSWORD) {
      setPasswordError('Incorrect password. Please try again.');
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/admin/clients/${clientToDelete.id}`,
        {
          method: 'DELETE',
          headers
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`User "${clientToDelete.name}" and all their data deleted successfully!\n\nDeleted:\n- ${data.stats.deleted_files} files\n- ${data.stats.deleted_documents} documents\n- ${data.stats.deleted_service_requests} service requests\n- ${data.stats.deleted_payments} payments\n- ${data.stats.deleted_tickets} tickets`);
        
        // Reload data
        await loadData();
        
        // Close modal if viewing this client
        if (selectedClient === clientToDelete.id) {
          setSelectedClient(null);
          setClientDetails(null);
        }
      } else {
        alert(`Failed to delete user: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('An error occurred while deleting the user. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setClientToDelete(null);
      setDeletePassword('');
      setPasswordError('');
    }
  };

  const handleRespondToTicket = async () => {
    if (!selectedTicket || !ticketResponse.trim()) {
      alert('Please enter a response');
      return;
    }

    setRespondingToTicket(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/admin/tickets/${selectedTicket.id}/respond`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            service_request_id: selectedTicket.service_request_id,
            response: ticketResponse
          })
        }
      );

      if (response.ok) {
        alert('Response sent successfully!');
        
        // Reload tickets
        await loadData();
        
        // Close modal
        setSelectedTicket(null);
        setTicketResponse('');
      } else {
        const data = await response.json();
        alert(`Failed to send response: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error responding to ticket:', error);
      alert('An error occurred while sending response. Please try again.');
    } finally {
      setRespondingToTicket(false);
    }
  };

  const openTicketsModal = () => {
    setShowTicketsModal(true);
  };

  const getOpenTicketsCount = () => {
    return tickets.filter(t => t.status === 'open').length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-600">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage clients, orders, and documents</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Clients</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="text-blue-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Paid Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.paid_orders}</p>
                  <p className="text-xs text-gray-500 mt-1">{stats.pending_orders} pending</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <Package className="text-green-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Documents</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_documents}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <FileText className="text-purple-600" size={28} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-gray-900">₹{stats.total_revenue.toLocaleString()}</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg">
                  <DollarSign className="text-orange-600" size={28} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tickets Button */}
        <div className="mb-6">
          <button
            onClick={openTicketsModal}
            className="relative bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-3"
          >
            <MessageSquare size={24} />
            <span className="font-semibold">View Support Tickets</span>
            {getOpenTicketsCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center animate-pulse">
                {getOpenTicketsCount()}
              </span>
            )}
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, mobile, or client ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" size={20} />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Clients</option>
                <option value="active">Active (Paid Orders)</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client ID
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid Orders
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500">{client.pan}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email}</div>
                      <div className="text-sm text-gray-500">{client.mobile}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900">{client.client_id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        client.total_paid_orders > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {client.total_paid_orders}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 text-purple-800">
                        {client.total_documents}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => viewClientDetails(client.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <Eye size={16} />
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                          title="Delete user and all their data"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredClients.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No clients found matching your search</p>
            </div>
          )}
        </div>
      </div>

      {/* Client Details Modal */}
      <Dialog open={selectedClient !== null} onOpenChange={() => {
        setSelectedClient(null);
        setClientDetails(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
            <DialogDescription>
              View client information, orders, and documents
            </DialogDescription>
          </DialogHeader>

          {clientDetails && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{clientDetails.user.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Client ID:</span>
                    <span className="ml-2 font-mono font-medium">{clientDetails.user.client_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2">{clientDetails.user.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Mobile:</span>
                    <span className="ml-2">{clientDetails.user.mobile}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">PAN:</span>
                    <span className="ml-2 font-mono">{clientDetails.user.pan}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">DOB:</span>
                    <span className="ml-2">{clientDetails.user.dob}</span>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{clientDetails.summary.total_orders}</p>
                  <p className="text-xs text-gray-600 mt-1">Total Orders</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{clientDetails.summary.paid_orders}</p>
                  <p className="text-xs text-gray-600 mt-1">Paid Orders</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{clientDetails.summary.total_documents}</p>
                  <p className="text-xs text-gray-600 mt-1">Documents</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{clientDetails.summary.total_payments}</p>
                  <p className="text-xs text-gray-600 mt-1">Payments</p>
                </div>
              </div>


              {/* Pending Orders */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3 text-orange-600">⏰ Pending Orders</h3>
                {clientDetails.service_requests
                  .filter(order => order.payment_status === 'pending')
                  .map((order) => {
                    const orderDocs = clientDetails.documents.filter(
                      doc => doc.service_request_id === order.id
                    );
                    return (
                      <div key={order.id} className="border-2 border-orange-300 rounded-lg p-4 mb-3 bg-orange-50">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{order.service_name || 'Service'}</h4>
                            <p className="text-sm text-gray-600">
                              Request ID: <code className="text-xs font-mono bg-white px-2 py-0.5 rounded break-all">{order.id}</code>
                            </p>
                            <p className="text-sm text-gray-600">
                              Order Date: {new Date(order.created_at).toLocaleString('en-IN', { 
                                dateStyle: 'medium', 
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                            {order.custom_amount && (
                              <p className="text-sm font-bold text-orange-700 mt-1">
                                Custom Amount: ₹{order.custom_amount}
                              </p>
                            )}
                            {order.price && (
                              <p className="text-sm text-gray-600">
                                Base Price: ₹{order.price}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-semibold rounded-full">
                              Pending
                            </span>
                            <button
                              onClick={() => handleSetPayment(order)}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded transition-colors"
                              title="Set custom payment amount and link for extra work"
                            >
                              💳 Set Custom Billing
                            </button>
                          </div>
                        </div>

                        {orderDocs.length > 0 && (
                          <div className="space-y-2 bg-white p-3 rounded-lg mt-3">
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <FileText size={16} className="text-orange-600" />
                              Documents ({orderDocs.length}):
                            </p>
                            {orderDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between bg-gray-50 p-2 rounded border"
                              >
                                <span className="text-sm text-gray-700">{doc.original_filename || doc.doc_type}</span>
                                <button
                                  onClick={() => downloadDocument(doc.id, doc.original_filename || doc.doc_type)}
                                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                                >
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {clientDetails.service_requests.filter(o => o.payment_status === 'pending').length === 0 && (
                  <p className="text-gray-500 text-center py-4 text-sm italic">No pending orders</p>
                )}
              </div>

              {/* Payment Under Review Orders */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3 text-blue-600">⏳ Payment Under Review</h3>
                {clientDetails.service_requests
                  .filter(order => order.payment_status === 'under_review')
                  .map((order) => {
                    const orderDocs = clientDetails.documents.filter(
                      doc => doc.service_request_id === order.id
                    );
                    return (
                      <div key={order.id} className="border-2 border-blue-300 rounded-lg p-4 mb-3 bg-blue-50">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{order.service_name || 'Service'}</h4>
                            <p className="text-sm text-gray-600">
                              Request ID: <code className="text-xs font-mono bg-white px-2 py-0.5 rounded break-all">{order.id}</code>
                            </p>
                            <p className="text-sm text-gray-600">
                              Order Date: {new Date(order.created_at).toLocaleString('en-IN', { 
                                dateStyle: 'medium', 
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                            {order.custom_amount && (
                              <p className="text-sm font-bold text-blue-700 mt-1">
                                Amount: ₹{order.custom_amount}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-semibold rounded-full">
                              Under Review
                            </span>
                            <button
                              onClick={async () => {
                                if (window.confirm('Mark this payment as verified and paid?')) {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(
                                      `${BACKEND_URL}/api/admin/orders/${order.id}/mark-paid`,
                                      {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`
                                        }
                                      }
                                    );
                                    if (response.ok) {
                                      alert('✓ Payment verified! Order marked as paid. Client has been notified.');
                                      viewClientDetails(selectedClient);
                                      loadData();
                                    } else {
                                      throw new Error('Failed to mark as paid');
                                    }
                                  } catch (error) {
                                    console.error('Error marking as paid:', error);
                                    alert('Failed to mark as paid. Please try again.');
                                  }
                                }
                              }}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded transition-colors"
                              title="Verify payment and mark as paid"
                            >
                              ✓ Mark as Paid
                            </button>
                          </div>
                        </div>

                        {orderDocs.length > 0 && (
                          <div className="space-y-2 bg-white p-3 rounded-lg mt-3">
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <FileText size={16} className="text-blue-600" />
                              Documents ({orderDocs.length}):
                            </p>
                            {orderDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between bg-gray-50 p-2 rounded border"
                              >
                                <span className="text-sm text-gray-700">{doc.original_filename || doc.doc_type}</span>
                                <button
                                  onClick={() => downloadDocument(doc.id, doc.original_filename || doc.doc_type)}
                                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                                >
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {clientDetails.service_requests.filter(o => o.payment_status === 'under_review').length === 0 && (
                  <p className="text-gray-500 text-center py-4 text-sm italic">No payments under review</p>
                )}
              </div>

              {/* Paid Orders with Documents */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Paid Orders & Documents</h3>
                {clientDetails.service_requests
                  .filter(order => order.payment_status === 'paid')
                  .map((order) => {
                    const orderDocs = clientDetails.documents.filter(
                      doc => doc.service_request_id === order.id
                    );
                    return (
                      <div key={order.id} className="border rounded-lg p-4 mb-3 bg-white shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{order.service_name || 'Service'}</h4>
                            <p className="text-sm text-gray-600">
                              Request ID: <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded break-all">{order.id}</code>
                            </p>
                            <p className="text-sm text-gray-600">
                              Order Date: {new Date(order.created_at).toLocaleString('en-IN', { 
                                dateStyle: 'medium', 
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                            <p className="text-sm text-gray-600">
                              Price: ₹{order.price || 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                              Paid
                            </span>
                            <button
                              onClick={() => handleSetPayment(order)}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded transition-colors"
                              title="Set custom payment amount and link"
                            >
                              💳 Billing
                            </button>
                            <button
                              onClick={() => markOrderAsUnpaid(order.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded transition-colors"
                              title="Mark as unpaid (allows user to delete)"
                            >
                              Mark as Unpaid
                            </button>
                          </div>
                        </div>

                        {orderDocs.length > 0 ? (
                          <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <FileText size={16} className="text-blue-600" />
                              Uploaded Documents ({orderDocs.length}):
                            </p>
                            {orderDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 hover:border-blue-300 transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <FileText className="text-blue-600" size={20} />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{doc.doc_type}</p>
                                    <p className="text-xs text-gray-500">
                                      Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                                    </p>
                                    {doc.password && (
                                      <div className="mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                        <span className="text-xs text-amber-800 font-medium">🔒 Password: </span>
                                        <code className="text-xs font-mono text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded">
                                          {doc.password}
                                        </code>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => downloadDocument(doc.id, doc.original_filename || doc.doc_type)}
                                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                                >
                                  <Download size={14} />
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded text-center">
                            <p className="text-sm text-gray-500 italic">No documents uploaded yet for this order</p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {clientDetails.service_requests.filter(o => o.payment_status === 'paid').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No paid orders</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="bg-red-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Trash2 className="text-red-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">Delete User?</h2>
              <p className="text-gray-600">
                Are you sure you want to delete <strong className="text-red-600">{clientToDelete?.name}</strong>?
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 font-semibold mb-2">⚠️ This action cannot be undone!</p>
              <p className="text-sm text-red-700">
                This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 ml-4 list-disc">
                <li>User account and profile</li>
                <li>All service requests</li>
                <li>All uploaded documents and files</li>
                <li>All payments history</li>
                <li>All support tickets</li>
              </ul>
            </div>
            
            {/* Password Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Admin Password to Confirm <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => {
                  setDeletePassword(e.target.value);
                  setPasswordError('');
                }}
                placeholder="Admin password required"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  passwordError ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={deleting}
              />
              {passwordError && (
                <p className="text-red-600 text-sm mt-1">{passwordError}</p>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setClientToDelete(null);
                  setDeletePassword('');
                  setPasswordError('');
                }}
                disabled={deleting}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteClient}
                disabled={deleting || !deletePassword}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Modal */}
      {showTicketsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Support Tickets</h2>
                <p className="text-gray-600 text-sm mt-1">
                  {getOpenTicketsCount()} open tickets • {tickets.length} total
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTicketsModal(false);
                  setSelectedTicket(null);
                  setTicketResponse('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {tickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-gray-600">No support tickets yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className={`border rounded-lg p-4 ${
                        ticket.status === 'open' ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{ticket.subject}</h3>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                ticket.status === 'open'
                                  ? 'bg-orange-200 text-orange-800'
                                  : 'bg-green-200 text-green-800'
                              }`}
                            >
                              {ticket.status === 'open' ? '🔔 Open' : '✓ Responded'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><strong>Customer:</strong> {ticket.user_name} ({ticket.user_email})</p>
                            <p><strong>Mobile:</strong> {ticket.user_mobile}</p>
                            <p><strong>Request ID:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{ticket.service_request_id}</code></p>
                            <p><strong>Raised:</strong> {new Date(ticket.created_at).toLocaleString('en-IN', { 
                              dateStyle: 'medium', 
                              timeStyle: 'short',
                              timeZone: 'Asia/Kolkata'
                            })}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm text-gray-700 mb-2"><strong>Message:</strong></p>
                        <p className="text-gray-700 bg-white p-3 rounded border">{ticket.message}</p>
                      </div>

                      {ticket.admin_response ? (
                        <div className="bg-green-50 border border-green-200 rounded p-3">
                          <p className="text-sm text-green-800 mb-1">
                            <strong>Your Response:</strong>
                          </p>
                          <p className="text-gray-700">{ticket.admin_response}</p>
                          <p className="text-xs text-green-700 mt-2">
                            Responded: {new Date(ticket.admin_response_at).toLocaleString('en-IN', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                              timeZone: 'Asia/Kolkata'
                            })}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedTicket?.id === ticket.id ? (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Your Response:
                              </label>
                              <textarea
                                value={ticketResponse}
                                onChange={(e) => setTicketResponse(e.target.value)}
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Type your response here..."
                              />
                              <div className="flex gap-2 mt-3">
                                <button
                                  onClick={handleRespondToTicket}
                                  disabled={respondingToTicket || !ticketResponse.trim()}
                                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                  <Send size={16} />
                                  {respondingToTicket ? 'Sending...' : 'Send Response'}
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedTicket(null);
                                    setTicketResponse('');
                                  }}
                                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setSelectedTicket(ticket)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                            >
                              <MessageSquare size={16} />
                              Respond to Ticket
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Set Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2 text-gray-800 flex items-center gap-2">
                💳 Set Custom Fees
              </h2>
              <p className="text-gray-600 text-sm">
                Set custom fee amount for: <strong>{selectedOrder?.service_name}</strong>
              </p>
              <p className="text-gray-500 text-xs mt-2">
                QR code will be auto-generated with UPI: 8892117903@ybl
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount (e.g., 2449)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-800">
                    ✓ UPI QR code will be automatically generated for this amount
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Client will scan QR, pay, and enter transaction ID
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={submitPaymentDetails}
                disabled={settingPayment || !customAmount}
                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {settingPayment ? 'Setting Fees...' : '✓ Set Custom Fees'}
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setCustomAmount('');
                  setPaymentLink('');
                }}
                disabled={settingPayment}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
